import { useEffect, useState } from "react";
import { creerLecteurContinu } from "../utils/continuationPlayback";

/**
 * Scène du Tore-Spirale — REÇUE du backend, jamais redéfinie ici.
 *
 * Voir docs/DOCTRINE_GEOMETRIE_CANONIQUE.md et docs/PROCEDURE_CONFORMITE.md.
 *
 * ⚠️ Ce hook construisait sa propre courbe : `radius = 3`, 5 tours, 200 points,
 * calculée dans un `useMemo` — donc STATIQUE. Le module portait alors cinq
 * définitions concurrentes de sa scène, dont aucune ne coïncidait avec une
 * autre : le rayon valait 3, 5 ou 8 selon la source, la résolution 24, 120 ou
 * 200.
 *
 * Et la courbe affichée n'avait que **40 positions distinctes** pour 200
 * objets. `theta` parcourait 5 révolutions tandis que la phase de tube valait
 * `3·theta` : les deux étant 2π-périodiques, la courbe se retraçait 5 fois et
 * les sphères s'empilaient par paquets de 5.
 *
 * Il produisait aussi un tableau `velocitiesArray` qui n'était pas des
 * vitesses : la différence SPATIALE entre deux points consécutifs de la courbe,
 * soit un vecteur tangent. Cette quantité partait ensuite vers le backend comme
 * si elle avait été mesurée.
 *
 * La forme canonique — arbitrage produit du 2026-07-29, option C, proportions
 * C2 — est un objet à DEUX BRINS entrelacés sur un même tore : 100 sphères en
 * `phi = 3·theta`, 100 cubes en `phi = 3·theta + π`. Le backend la génère, la
 * fait vivre, et la sert. Le front la rend.
 *
 * Les curseurs paramètrent le MOTEUR (règle 2) : ils sont transmis à
 * `/animate`, pas appliqués au rejeu. `flashRate` et les paramètres de bloom
 * n'y figurent pas — ce sont des effets de rendu, ils n'ont rien à faire dans
 * le moteur, et ils entraient auparavant dans l'entropie dynamique.
 */
export default function useSpiraleTorusVisualizer({
  spiralSpeedPct = 50,
  chaos = false,
  steps = 240,
} = {}) {
  const [scene, setScene] = useState({
    objects: [],
    nParBrin: 0,
    torus: null,
    lecteur: null,
  });

  useEffect(() => {
    const controller = new AbortController();
    const json = (r) =>
      r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`));

    fetch("/api/geometry/spiral_torus/initial", { signal: controller.signal })
      .then(json)
      .then((data) => {
        if (!Array.isArray(data.objects) || data.objects.length === 0) {
          throw new Error("réponse /initial invalide");
        }

        const params = new URLSearchParams({
          steps: String(steps),
          dt: "0.02",
          spiral_speed: String(spiralSpeedPct / 50), // 0..100 % → 0..2
          chaos: chaos ? "true" : "false",
        });

        // ⚠️ LECTURE CONTINUE — le rejeu d'un segment unique téléportait la
        // scène à la couture : 205x le pas typique, le PIRE des huit modules.
        // Voir docs/SPEC_CONTINUATION.md.
        const lecteur = creerLecteurContinu({
          url: "/api/geometry/spiral_torus/animate",
          parametres: () => Object.fromEntries(params),
        });
        lecteur.demarrer();
        setScene({
          objects: data.objects,
          nParBrin: data.n_par_brin,
          torus: data.torus,
          lecteur,
        });
        return null;
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Erreur scène spiral_torus:", err);
        }
      });

    return () => {
      controller.abort();
      // Arrêter le lecteur : il porte une requête de préchargement en vol.
      setScene((precedent) => {
        if (precedent.lecteur) precedent.lecteur.arreter();
        return { ...precedent, lecteur: null };
      });
    };
  }, [spiralSpeedPct, chaos, steps]);

  return scene;
}
